import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { getSummary, getHomeStatConfig, getVcCount } from '../../Utils/frontStageApi';

export type StatCardItem = {
  key: string;
  unitLabel: string;
  unitValue: string;
  totalLabel: string;
  totalValue: string;
};

type HomeStatisticsView = {
  certIssuanceUnits: number;
  totalCertIssuances: number | null;
  vcIssuanceUnits: number;
  totalVcIssuances: number | null;
  vcVerificationUnits: number;
  totalVcVerifications: number | null;
};

const defaultStats: HomeStatisticsView = {
  certIssuanceUnits: 0,
  totalCertIssuances: null,
  vcIssuanceUnits: 0,
  totalVcIssuances: 0,
  vcVerificationUnits: 0,
  totalVcVerifications: 0,
};

const RoundedStatNumber: React.FC<{ value: string; color?: string }> = ({ value, color = '#00AAFF' }) => (
  <Flex
    w="100%"
    minW="260px"
    h="72px"
    bg="#FFF9D0"
    justify="center"
    align="center"
    borderTopLeftRadius="50px"
    borderTopRightRadius="50px"
    borderBottomLeftRadius="0"
    borderBottomRightRadius="0"
  >
    <Text
      fontFamily="'Open Sans'"
      fontWeight={700}
      fontSize="54px"
      lineHeight="100%"
      letterSpacing="0.01em"
      color={color}
      textAlign="center"
    >
      {value}
    </Text>
  </Flex>
);

export const StatCard: React.FC<StatCardItem> = ({ unitLabel, unitValue, totalLabel, totalValue }) => (
  <Flex w="100%" direction="column" align="center" gap="18px">
    <Flex direction="column" align="center" gap="15px" w="100%">
      <Flex direction="column" align="center" gap="5px" py="20px">
        <Text
          fontFamily="'Open Sans'"
          fontWeight={600}
          fontSize="20px"
          lineHeight="120%"
          color="#000000"
          textAlign="center"
        >
          {unitLabel}
        </Text>
        <RoundedStatNumber value={unitValue} color="#00AAFF" />
      </Flex>

      <Flex direction="column" align="center" gap="4px">
        <Text
          fontFamily="'Open Sans'"
          fontWeight={600}
          fontSize="20px"
          lineHeight="120%"
          color="#000000"
          textAlign="center"
        >
          {totalLabel}
        </Text>
        <RoundedStatNumber value={totalValue} color="#003F8C" />
      </Flex>
    </Flex>
  </Flex>
);

export const useStatCards = (): { cards: StatCardItem[]; loading: boolean } => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<HomeStatisticsView>(defaultStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchAllStats = async () => {
      setLoading(true);
      try {
        const [summaryRes, configRes, vcCountRes] = await Promise.all([
          getSummary(),
          getHomeStatConfig(),
          getVcCount(),
        ]);
        if (!mounted) return;

        // 不重複發行單位數：Chinese／English 兩個 dict 的 key（issuer id）取聯集
        const chineseCounts = vcCountRes?.Chinese ?? {};
        const englishCounts = vcCountRes?.English ?? {};
        const uniqueIssuerCount = new Set([...Object.keys(chineseCounts), ...Object.keys(englishCounts)]).size;

        // 總發證次數：兩個 dict 的 value 全部加總
        const totalVcIssuances =
          Object.values(chineseCounts).reduce((sum, n) => sum + n, 0) +
          Object.values(englishCounts).reduce((sum, n) => sum + n, 0);

        setStats({
          certIssuanceUnits: summaryRes?.applied ? Number(summaryRes.issuer_num ?? 0) : 0,
          totalCertIssuances: summaryRes?.applied ? Number(summaryRes.issued_cnt ?? 0) : null,
          vcIssuanceUnits: uniqueIssuerCount,
          totalVcIssuances,
          vcVerificationUnits: configRes?.applied ? Number(configRes.vc_verification_units ?? 0) : 0,
          totalVcVerifications: 0,
        });
      } catch {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAllStats();
    return () => {
      mounted = false;
    };
  }, []);

  const cards: StatCardItem[] = useMemo(
    () => [
      {
        key: 'apply',
        unitLabel: t('statistics.vcIssuanceUnits'),
        unitValue: String(stats.vcIssuanceUnits),
        totalLabel: t('statistics.totalIssuances'),
        totalValue: stats.totalVcIssuances === null ? '--' : String(stats.totalVcIssuances),
      },
      {
        key: 'verify',
        unitLabel: t('statistics.vcVerificationUnits'),
        unitValue: String(stats.vcVerificationUnits),
        totalLabel: t('statistics.totalVerifications'),
        totalValue: stats.totalVcVerifications === null ? '--' : String(stats.totalVcVerifications),
      },
      {
        key: 'document',
        unitLabel: t('statistics.certIssuanceUnits'),
        unitValue: String(stats.certIssuanceUnits),
        totalLabel: t('statistics.totalIssuances'),
        totalValue: stats.totalCertIssuances === null ? '--' : String(stats.totalCertIssuances),
      },
    ],
    [stats, t],
  );

  return { cards, loading };
};
